import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import CreateOrderModal from './Modals/CreateOrderModal';
import { OrderContext } from '../../../../context/OrderContext';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DeleteOrderModal from './Modals/DeleteOrderModal';
import { CompanyContext } from '../../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { OrderExportModal } from './Modals/OrderExportModal';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import {
  ExportOrderResponse,
  ExportOrderVariables,
  ReportMutations,
  CsvExportFile,
} from '../../../../graphql/export.graphql';
import { DownloadMultipleTextAsZip } from '../../../../util/download.util';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { orderTableHeaders } from '../../Common/OrderTable';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { Order } from '../../../../types/order/order';
import ImportOrderModal from './Modals/OrderImportModal';
import { ProductTransactionContext } from '../../../../context/ProductTransactionContext';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import { UserContext } from '../../../../context/UserContext';
import Menu, { ImportMenuItemTemplate, MenuItemProps } from '../../../../VentoryUI/components/common/Menu';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { OrderFilter } from '../../../../VentoryUI/components/filters/OrderFilter';
import Table from '../../../components/Table';
import ImportIcon from '../../../../VentoryUI/icons/Import/ImportIcon';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { Routing } from '../../../../util/routes.util';
import { DeleteIconButton, SettingsIconButton } from '../../../icons';
import { Button, CreateButton, ExportIconButton } from '../../../components/Button';
import { Elteros } from '../../../../types/customer/Elteros/Elteros';
import StockReservationModal from './Modals/StockReservationModal';
import { ContactContext } from '../../../../context/ContactContext';
import { Unilever } from '../../../../types/customer/DHL/Unilever/Unilever';
import ProcessMessageModal from './Modals/ProcessMessageModal';
import { TableSettingsContext } from '../../../../context/TableSettingsContext';
import { EntityType } from '../../../../types/comment';

export default function OrdeOverviewPane() {
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const filterParam = params.get('filter') || '';

  const { orders, setOrders, ordersLoading, loadAllOrders } = useContext(OrderContext);
  const { productTransactionsLoading, loadAllProductTransactions } = useContext(ProductTransactionContext);
  const { currentCompany, companies } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRoleInCompany } = useContext(StockLocationRoleAssignmentContext);
  const { getTableSettings } = useContext(TableSettingsContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { contacts } = useContext(ContactContext);
  const { locale } = useContext(UserContext);

  const orderTableSettings = getTableSettings(EntityType.order, currentCompany.id);

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [openStockReservationModal, setOpenStockReservationModal] = useState<boolean>(false);
  const [openProcessMessageModal, setOpenProcessMessageModal] = useState<boolean>(false);

  const [items, setItems] = useState<Order[]>([...orders.values()]);

  const [download, { loading: downloadLoading }] = useMutation<ExportOrderResponse, ExportOrderVariables>(
    ReportMutations.order,
  );

  const handleFileDownload = (files: CsvExportFile[]) => {
    DownloadMultipleTextAsZip(
      files.map(f => {
        return { name: f.fileName, text: f.content };
      }),
      `ventory_orders_${new Date().toISOString()}.zip`,
    );
  };

  const handleDownload = async () => {
    const files: CsvExportFile[] = [];
    let hasNext = true;
    let page = 0;

    while (hasNext) {
      const response = await download({
        variables: {
          orderIds: [...selected.values()],
          page: page,
          timezone: moment.tz.guess(),
          locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
          companyId: currentCompany.id,
          configurationIds: [...selected.values()],
        },
      });
      files.push(...(response.data?.generateOrderExport.data || []));

      hasNext = response.data?.generateOrderExport.hasNext || false;
      page++;
    }

    if (!files.length) return console.error('No files to download');

    handleFileDownload(files);

    setSelected(new Set());
  };

  const loadAll = async () => {
    loadAllProductTransactions();
    loadAllOrders();
  };

  const isSupervisor = hasStockLocationRoleInCompany(currentCompany.id, StockLocationRole.STOCK_LOCATION_SUPERVISOR);
  const isManager = hasStockLocationRoleInCompany(currentCompany.id, StockLocationRole.STOCK_LOCATION_MANAGER);

  const showElterosStockReservation = () => {
    const manager = hasStockLocationRoleInCompany(
      currentCompany.id,
      StockLocationRole.STOCK_LOCATION_MANAGER,
      Elteros.w2wStockLocationId,
    );

    return currentCompany.id === Elteros.companyId && (isAdmin || manager);
  };

  const menuItems: MenuItemProps[] = useMemo(() => {
    const shown: MenuItemProps[] = [];

    if (isSupervisor) {
      shown.push(ImportMenuItemTemplate(() => setOpenImportModal(true)));
      shown.push({
        text: t(TranslationKey.loadAll).singular.label,
        icon: <ImportIcon />,
        onClick: loadAll,
      });
    }

    return [...shown];
  }, [stockLocations]);

  const allItems = useMemo(() => {
    return [...orders.values()];
  }, [orders]);

  const hasSelected = selected.size;

  const filter = new OrderFilter(
    stockLocations,
    orderTableSettings,
    tagRelations,
    companyUsers,
    customFields,
    orders,
    currentCompany,
  );

  const showCreate = !Unilever.companyIds.includes(currentCompany.id);
  const showDelete = isAdmin && hasSelected;

  return (
    <>
      <CreateOrderModal open={openCreateModal} setOpen={setOpenCreateModal} />

      <DeleteOrderModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        setSelected={setSelected}
        ids={selected}
      />
      <OrderExportModal open={openExportModal} setOpen={setOpenExportModal} ids={selected} />
      <ImportOrderModal open={openImportModal} setOpen={setOpenImportModal} />
      {openStockReservationModal && (
        <StockReservationModal open={openStockReservationModal} setOpen={setOpenStockReservationModal} />
      )}
      {openProcessMessageModal && (
        <ProcessMessageModal open={openProcessMessageModal} setOpen={setOpenProcessMessageModal} />
      )}

      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={ordersLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterOrders).singular.label}
            filter={filter}
          />
          <div className='flex gap-1'>
            {showElterosStockReservation() ? (
              <Button text={'Stock Reservation'} onClick={() => setOpenStockReservationModal(true)} />
            ) : null}
            {Unilever.companyIds.includes(currentCompany.id) && isAdmin ? (
              <Button text={'Process Message'} onClick={() => setOpenProcessMessageModal(true)} />
            ) : null}
            {showDelete ? <DeleteIconButton onClick={() => setOpenDeleteModal(true)} /> : null}
            {hasSelected ? <ExportIconButton onClick={() => setOpenExportModal(true)} /> : null}
            {isAdmin ? <Menu items={menuItems} /> : null}
            {showCreate ? <CreateButton onClick={() => setOpenCreateModal(true)} /> : null}
            {isAdmin ? (
              <SettingsIconButton onClick={() => navigate(Routing.operations.orders.settings.overview())} />
            ) : null}
          </div>
        </div>
        <Table
          title={t(TranslationKey.order).plural.label}
          loading={ordersLoading || productTransactionsLoading}
          items={items}
          headers={orderTableHeaders(
            stockLocations,
            companyUsers,
            contacts,
            orderTableSettings,
            companies,
            currentCompany,
            locale,
          )}
          onClick={item => {
            navigate(Routing.operations.orders.update(item.id).detail());
          }}
          selectedValues={selected}
          onSelected={items => setSelected(new Set(items))}
          totalItemCount={allItems.length}
          filter={filter}
        />
      </div>
    </>
  );
}
